<template>
    <sub-section :title="listHeaderText"
                  helpTitle="Your final salary pensions"
                  :helpText="htmlArray('haves.final-salary.help')">
        <div v-for="finalSalary in getFinalSalaries"
              v-bind:key="finalSalary.id"
              class="row mb-2">
            <div class="col-9 d-flex align-items-center">
                {{ finalSalary.description }}
            </div>
            <div class="col-3">
                <three-dot-menu
                  :id="finalSalary.id"
                  :aria-purpose="finalSalary.name"
                  :permissions="getPermissionSet(finalSalary.editMode, false, true, true)"
                  @editClicked="onEdit"
                  @deleteClicked="onDelete" />
            </div>
        </div>

        <div v-show="!hasFinalSalaries">
            <empty-asset name="final salary pensions" />
        </div>

        <div class="row mt-4">
            <add-asset name="pension"
                        longName="another final salary pension"
                        @addClicked="onAdd()"
                        ref="addAssetComponent" />
        </div>
    </sub-section>

    <modal :title="modalTitle" ref="assetModal" idKey="finalSalary" close-button-aria-label="Close final salary popup editor" :returnFocusTo="addAssetButton">
        <final-salary-editor ref="assetEditor" @edit-finished="onEditFinished" />
    </modal>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import Modal from "@/guiide/components/common/Modal.vue";
import FinalSalaryEditor from "@/guiide/components/assets/FinalSalaryEditor.vue";
import { FinalSalary } from "@/guiide/ts/finalSalary";
import Constants from "@/guiide/ts/constants";
import SubSection from "@/guiide/components/common/SubSection.vue";
import AddAsset from "@/guiide/components/assets/AddAsset.vue";
import EmptyAsset from "@/guiide/components/assets/EmptyAsset.vue";
import ThreeDotMenu from "@/guiide/components/common/ThreeDotMenu.vue";
import { app } from "@/guiide/ts/app";
import TemporalDetail from "@/guiide/ts/temporalDetail";
import { getPermissionSet } from "@/guiide/ts/utilities";
import { useUIContent } from "@/guiide/ts/configuration/useUIContent";

export default defineComponent({
  name: "FinalSalaryAssetsViewer",
  components: {
    Modal,
    FinalSalaryEditor,
    SubSection,
    AddAsset,
    EmptyAsset,
    ThreeDotMenu
  },
  setup() {
    const assetModal = ref(null);
    const assetEditor = ref(null);
    const { htmlArray } = useUIContent();

    return {
      assetModal,
      assetEditor,
      getPermissionSet,
      htmlArray
    };
  },
  data() {
    return {
      modalTitle: "Edit final salary pension",
      addAssetButton: ref<HTMLElement>(null)
    };
  },
  methods: {
    onEditFinished() {
      this.assetModal.closeModal();
    },
    onAdd() {
      const defaultPaymentDate = TemporalDetail.getDefaultBenefitPaymentDate();
      this.modalTitle = "Add final salary pension";
      const incMonthIndxToAlignWithIncorrectMonthYearComponent = defaultPaymentDate.getMonth() + 1;
      const finalSalaryAsset = ObjectBuilder.createFinalSalary(
        Constants.NewId,
        "",
        0,
        incMonthIndxToAlignWithIncorrectMonthYearComponent,
        defaultPaymentDate.getFullYear(),
        Constants.LinkedToInflation
      );
      this.assetEditor.setValues(finalSalaryAsset, this.getRetirementYear());

      const addAssetComponent = this.$refs.addAssetComponent as any;
      this.addAssetButton = addAssetComponent.getAddButton();

      this.assetModal.showModal();
    },
    onEdit(id: number) {
      this.modalTitle = "Edit final salary pension";
      const finalSalaryAsset = this.getFinalSalaries.filter(p => p.id == id)[0];
      this.assetEditor.setValues(finalSalaryAsset, this.getRetirementYear());
      this.assetModal.showModal();
    },
    async onDelete(id: number) {
      await app.deleteFinalSalary(id);
    },
    getRetirementYear(): number {
      return new Date(this.$store.state.retiree.retirementDate).getFullYear();
    }
  },
  computed: {
    getFinalSalaries(): Array<FinalSalary> {
      return (
        this.$store.state.retiree?.finalSalaries.assets ?? new Array<FinalSalary>()
      );
    },
    hasFinalSalaries(): boolean {
      return this.getFinalSalaries.length > 0;
    },
    listHeaderText(): string {
      return this.$store.state.configuration.havesFinalSalaryListHeaderText;
    }
  }
});
</script>
